/*
Copyright (c) NAVER Corp.
name: @egjs/vue-infinitegrid
license: MIT
author: NAVER Corp.
repository: https://github.com/naver/egjs-infinitegrid/tree/master/packages/vue-infinitegrid
version: 3.3.0
*/
import NativeInfiniteGrid, { categorize, ItemManager, INFINITEGRID_EVENTS, CONTAINER_CLASSNAME, GridLayout as GridLayout$1, withInfiniteGridMethods, JustifiedLayout as JustifiedLayout$1, FrameLayout as FrameLayout$1, SquareLayout as SquareLayout$1, PackingLayout as PackingLayout$1 } from '@egjs/infinitegrid';
import { Prop, Component, Vue } from 'vue-property-decorator';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

/* global Reflect, Promise */
var extendStatics = function (d, b) {
  extendStatics = Object.setPrototypeOf || {
    __proto__: []
  } instanceof Array && function (d, b) {
    d.__proto__ = b;
  } || function (d, b) {
    for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p];
  };

  return extendStatics(d, b);
};

function __extends(d, b) {
  extendStatics(d, b);

  function __() {
    this.constructor = d;
  }

  d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}
var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }

    return t;
  };

  return __assign.apply(this, arguments);
};
function __decorate(decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function __read(o, n) {
  var m = typeof Symbol === "function" && o[Symbol.iterator];
  if (!m) return o;
  var i = m.call(o),
      r,
      ar = [],
      e;

  try {
    while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
  } catch (error) {
    e = {
      error: error
    };
  } finally {
    try {
      if (r && !r.done && (m = i["return"])) m.call(i);
    } finally {
      if (e) throw e.error;
    }
  }

  return ar;
}
function __spread() {
  for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));

  return ar;
}

var InfiniteGrid =
/*#__PURE__*/
function (_super) {
  __extends(InfiniteGrid, _super);

  function InfiniteGrid() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  var __proto = InfiniteGrid.prototype;

  __proto.mounted = function () {
    var wrapperElement = this.$el;
    this.$_nativeInfiniteGrid = new NativeInfiniteGrid(wrapperElement, __assign({}, this.options, {
      renderExternal: true
    }));
    this.$_wrapperElement = wrapperElement;
    this.$_layout = "";
    this.$_bindEvents();
    var nativeIG = this.$_nativeInfiniteGrid;
    nativeIG.setLayout(this.layoutType, this.layoutOptions);
    this.$_setLoadingElement();

    if (this.status) {
      nativeIG.setStatus(this.status, true, this.$_getElements());
    } else {
      nativeIG.beforeSync(this.$_toItems());
      nativeIG.layout(true);
    }
  };

  __proto.updated = function () {
    var nativeIG = this.$_nativeInfiniteGrid;
    var layout = this.$_layout;
    var elements = this.$_getElements();
    this.$_setLoadingElement();
    nativeIG.sync(elements);

    if (layout) {
      this.$_layout = "";
      nativeIG.layout(layout === "relayout");
    }
  };

  __proto.render = function (h) {
    var nativeIG = this.$_nativeInfiniteGrid;
    var items = this.$_toItems();
    var visibleChildren = [];

    if (nativeIG) {
      var result = nativeIG.beforeSync(items.filter(function (val) {
        return !val.isLoading;
      }));
      this.$_layout = result === "relayout" ? result : this.$_layout || result;
      visibleChildren = nativeIG.getRenderingItems().map(function (item) {
        return item.vnode;
      });

      if (this.$slots.loading) {
        visibleChildren.push.apply(visibleChildren, __spread(this.$slots.loading));
      }
    } else {
      var groups = categorize(items);

      if (this.status) {
        var _a = this.status._infinite,
            startCursor = _a.startCursor,
            endCursor = _a.endCursor;
        visibleChildren = ItemManager.pluck(groups.slice(startCursor, endCursor + 1), "items").map(function (item) {
          return item.vnode;
        });
      } else if (this.useFirstRender) {
        visibleChildren = items.map(function (item) {
          return item.vnode;
        });
      }
    } // To bypass
    // [Vue warn]: Avoid using observed data object as vnode data: {}


    var wrapperData = {};

    for (var key in this.wrapperData) {
      wrapperData[key] = this.wrapperData[key];
    }

    return h(this.tag, wrapperData, this.$_getContainer(visibleChildren, h));
  };

  __proto.beforeDestroy = function () {
    this.$_nativeInfiniteGrid.destroy();
  };

  __proto.setStatus = function (status, applyScrollPos, syncElements) {
    if (syncElements === void 0) {
      syncElements = this.$_getElements();
    }

    this.$_nativeInfiniteGrid.setStatus(status, applyScrollPos, syncElements);
    return this;
  };

  __proto.$_bindEvents = function () {
    var _this = this;

    var nativeIG = this.$_nativeInfiniteGrid;
    INFINITEGRID_EVENTS.forEach(function (eventName) {
      nativeIG.on(eventName, function (e) {
        e.currentTarget = _this; // Make events from camelCase to kebab-case

        _this.$emit(eventName.replace(/([A-Z])/g, "-$1").toLowerCase(), e);
      });
    });
    nativeIG.on("render", function (_a) {
      var next = _a.next;

      _this.$forceUpdate();

      _this.$nextTick(function () {
        next();
      });
    });
  };

  __proto.$_getContainer = function (children, h) {
    var _a;

    var isOverflowScroll = this.options.isOverflowScroll;

    if (!isOverflowScroll) {
      return children;
    }

    var containerData = {
      class: (_a = {}, _a[CONTAINER_CLASSNAME] = true, _a),
      ref: CONTAINER_CLASSNAME
    };
    return [h(this.containerTag, containerData, children)];
  };

  __proto.$_getElements = function () {
    var container = this.$refs && this.$refs[CONTAINER_CLASSNAME];
    var elements = [].slice.call((container || this.$_wrapperElement).children);

    if (this.$slots.loading) {
      return elements.slice(0, -1);
    }

    return elements;
  };

  __proto.$_toItems = function () {
    var _this = this;

    var items = this.$slots.default;

    if (!items) {
      return [];
    }

    return items.map(function (child, index) {
      var groupKey = _this.groupBy(child, index) || "";
      var itemKey = child.key != null ? child.key : child.tag + "-" + index;
      return {
        groupKey: groupKey,
        itemKey: itemKey,
        vnode: child
      };
    });
  };

  __proto.$_setLoadingElement = function () {
    var ig = this.$_nativeInfiniteGrid;

    if (this.$slots.loading) {
      var container = this.$refs && this.$refs[CONTAINER_CLASSNAME];
      var loadingElement = (container || this.$_wrapperElement).lastElementChild;

      if (loadingElement) {
        ig.setLoadingBar({
          append: loadingElement,
          prepend: loadingElement
        });
        return;
      }
    }

    ig.setLoadingBar();
  };

  __decorate([Prop({
    type: String,
    default: "div",
    required: false
  })], InfiniteGrid.prototype, "tag", void 0);

  __decorate([Prop({
    type: String,
    default: "div",
    required: false
  })], InfiniteGrid.prototype, "containerTag", void 0);

  __decorate([Prop({
    type: Boolean,
    default: false,
    required: false
  })], InfiniteGrid.prototype, "useFirstRender", void 0);

  __decorate([Prop({
    type: Object,
    default: null,
    required: false
  })], InfiniteGrid.prototype, "status", void 0);

  __decorate([Prop({
    type: Object,
    default: function () {
      return {};
    },
    required: false
  })], InfiniteGrid.prototype, "options", void 0);

  __decorate([Prop({
    type: Object,
    default: function () {
      return {};
    },
    required: false
  })], InfiniteGrid.prototype, "layoutOptions", void 0);

  __decorate([Prop({
    type: Function,
    default: GridLayout$1,
    required: false
  })], InfiniteGrid.prototype, "layoutType", void 0);

  __decorate([Prop({
    type: Object,
    default: function () {
      return {};
    },
    required: false
  })], InfiniteGrid.prototype, "wrapperData", void 0);

  __decorate([Prop({
    type: Function,
    default: function (item, index) {
      var attrs = item.data && item.data.attrs;
      var props = item.data && item.data.props; // For DOM elements

      if (attrs) {
        if ("data-groupkey" in attrs) {
          return attrs["data-groupkey"];
        } else if ("groupKey" in attrs) {
          return attrs.groupKey;
        }
      } // For components


      if (props) {
        if ("data-groupkey" in props) {
          return props["data-groupkey"];
        } else if ("groupKey" in props) {
          return props.groupKey;
        }
      }

      return "";
    }
  })], InfiniteGrid.prototype, "groupBy", void 0);

  __decorate([withInfiniteGridMethods], InfiniteGrid.prototype, "$_nativeInfiniteGrid", void 0);

  InfiniteGrid = __decorate([Component({})], InfiniteGrid);
  return InfiniteGrid;
}(Vue);

var GridLayout =
/*#__PURE__*/
function (_super) {
  __extends(GridLayout, _super);

  function GridLayout() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  __decorate([Prop({
    type: Function,
    default: GridLayout$1,
    required: false
  })], GridLayout.prototype, "layoutType", void 0);

  GridLayout = __decorate([Component({})], GridLayout);
  return GridLayout;
}(InfiniteGrid);

var JustifiedLayout =
/*#__PURE__*/
function (_super) {
  __extends(JustifiedLayout, _super);

  function JustifiedLayout() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  __decorate([Prop({
    type: Function,
    default: JustifiedLayout$1,
    required: false
  })], JustifiedLayout.prototype, "layoutType", void 0);

  JustifiedLayout = __decorate([Component({})], JustifiedLayout);
  return JustifiedLayout;
}(InfiniteGrid);

var FrameLayout =
/*#__PURE__*/
function (_super) {
  __extends(FrameLayout, _super);

  function FrameLayout() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  __decorate([Prop({
    type: Function,
    default: FrameLayout$1,
    required: false
  })], FrameLayout.prototype, "layoutType", void 0);

  FrameLayout = __decorate([Component({})], FrameLayout);
  return FrameLayout;
}(InfiniteGrid);

var SquareLayout =
/*#__PURE__*/
function (_super) {
  __extends(SquareLayout, _super);

  function SquareLayout() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  __decorate([Prop({
    type: Function,
    default: SquareLayout$1,
    required: false
  })], SquareLayout.prototype, "layoutType", void 0);

  SquareLayout = __decorate([Component({})], SquareLayout);
  return SquareLayout;
}(InfiniteGrid);

var PackingLayout =
/*#__PURE__*/
function (_super) {
  __extends(PackingLayout, _super);

  function PackingLayout() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  __decorate([Prop({
    type: Function,
    default: PackingLayout$1,
    required: false
  })], PackingLayout.prototype, "layoutType", void 0);

  PackingLayout = __decorate([Component({})], PackingLayout);
  return PackingLayout;
}(InfiniteGrid);

var version = "3.3.0";

var install = function (Vue) {
  Vue.component("InfiniteGrid", InfiniteGrid);
  Vue.component("GridLayout", GridLayout);
  Vue.component("JustifiedLayout", JustifiedLayout);
  Vue.component("FrameLayout", FrameLayout);
  Vue.component("SquareLayout", SquareLayout);
  Vue.component("PackingLayout", PackingLayout);
};

var plugin = {
  InfiniteGrid: InfiniteGrid,
  GridLayout: GridLayout,
  JustifiedLayout: JustifiedLayout,
  FrameLayout: FrameLayout,
  SquareLayout: SquareLayout,
  PackingLayout: PackingLayout,
  install: install,
  version: version
};

export default plugin;
export { FrameLayout, GridLayout, InfiniteGrid, JustifiedLayout, PackingLayout, SquareLayout, install, version };
//# sourceMappingURL=infinitegrid.esm.js.map
